import { Meta } from './meta.model'

// A conversão de um caderno de provas pelo microserviço de IA.

export type AiGenerationStatus =
	| 'created'
	| 'queued'
	| 'processing'
	| 'completed'
	| 'failed'
	| 'canceled'

// Os estados da geração como se leem na tela.
//
// O microserviço nomeia os seus em inglês, e crus fazem quem acompanha uma
// conversão ler "queued → processing" no meio de um texto em português.
export const AI_GENERATION_STATUS_LABELS: Record<AiGenerationStatus, string> = {
	created: 'Criada',
	queued: 'Na fila',
	processing: 'Convertendo',
	completed: 'Concluída',
	failed: 'Falhou',
	canceled: 'Cancelada',
}

// Os mesmos nomes, ditos como o despacho os vive.
//
// O histórico não conta a vida da geração: ele conta a do despacho, que só põe
// o caderno na fila de conversão e termina segundos depois de o arquivo
// chegar. Lido com as palavras da geração, o "completed" dele vira "Concluída"
// e anuncia como pronta uma prova cujas questões vão levar minutos para
// existir — enquanto o selo ao lado ainda diz "Na fila".
export const AI_DISPATCH_STATUS_LABELS: Record<AiGenerationStatus, string> = {
	created: 'Caderno recebido',
	queued: 'Caderno na fila',
	processing: 'Despachando o caderno',
	completed: 'Caderno despachado',
	failed: 'Despacho falhou',
	canceled: 'Despacho cancelado',
}

export const describeDispatchStatus = (status: string): string =>
	AI_DISPATCH_STATUS_LABELS[status as AiGenerationStatus] ?? status

export interface AiGeneration {
	id: number
	userId: string | null
	fileName: string
	answerKeyFileName: string | null
	answerKeyVariant: string | null
	ingestionId: string | null
	conversionId: string | null
	status: AiGenerationStatus
	error: string | null
	totalQuestions: number
	receivedQuestions: number
	examBank: string | null
	examYear: number | null
	examPosition: string | null
	examOrganization: string | null

	// O que o pedido inteiro custou e quanto durou. Não é a soma das questões:
	// ler o caderno acontece uma vez, é a etapa mais cara e não pertence a
	// questão nenhuma. A duração é de relógio, porque as questões rodam em
	// paralelo e somá-las daria mais que o tempo real.
	costUsd: number
	durationMs: number

	filterIds: number[]
	createdAt: string
	updatedAt: string
	admin?: { id: string; name: string } | null
}

// O estado do despacho do caderno para a fila de conversão, perguntado ao
// microserviço na hora. Não diz nada sobre as questões — o quanto já foi feito
// se lê dos contadores da própria geração. Nulo quer dizer que ele não
// respondeu, e a geração continua legível sem isso.
export interface AiGenerationDispatch {
	status: AiGenerationStatus
	isStale: boolean
	failureReason: string | null
}

export interface AiGenerationDetail extends AiGeneration {
	dispatch: AiGenerationDispatch | null
}

export interface AiGenerationListResponse {
	data: AiGeneration[]
	meta: Meta
}

export interface AiGenerationListRequest {
	page?: number
	limit?: number
	status?: string
	search?: string
}

export interface AiGenerationCreateRequest {
	examPdf: FileList
	answerKeyPdf: FileList
	answerKeyVariant?: string
	filterIds?: number[]

	// A quem a prova pertence: estado, município, órgão. Decide qual norma local
	// pode fundamentar a questão, e é o único dos metadados que a conversão não
	// consegue deduzir com segurança da capa do PDF.
	scopes?: string[]
}

export interface AiGenerationTransition {
	id: string
	ingestionId: string
	fromStatus: string | null
	toStatus: string
	reason: string | null
	createdAt: string
}

export interface AiGenerationHistoryResponse {
	transitions: AiGenerationTransition[]
	unavailable?: string
}

// O que a conversão quer que a curadoria saiba antes de aprovar a questão.
export interface QuestionAiReview {
	id: number
	questionId: number
	generationId: number
	externalId: string
	sourceNumber: number | null

	// A questão como a banca a publicou. A conversão reescreve — múltipla
	// escolha vira assertiva, e a alternativa que a originou some no texto novo
	// —, então sem o original ao lado a revisão julga de memória.
	sourceStatement: string | null
	sourceAlternatives: string | null
	sourceAnswer: string | null

	// Quem decidiu, o que decidiu e quando. Nulo enquanto ninguém olhou.
	decidedByAdminId: string | null
	decision: 'approved' | 'rejected' | null
	decidedAt: string | null
	decidedBy?: { id: string; name: string } | null

	reviewNote: string | null
	// Vem do MySQL como 0 ou 1, e não como booleano.
	foundationFromCorpus: boolean | number
	referenceLaw: string | null
	referenceArticle: string | null
	subjectName: string | null
	subSubjectName: string | null
	matchedSubMatterId: number | null
	costUsd: number
	durationMs: number
}

export interface AiGenerationQuestion {
	review: QuestionAiReview
	question: {
		id: number
		title: string
		description: string
		explanation: string
		isTrue: number | boolean
		isAccepted: number | boolean
		reasonRefused: string | null
		subMatters?: Array<{ id: number; name: string }>
		filters?: Array<{ id: number; name: string; typeId: number }>
	} | null
}

export interface AiGenerationQuestionsResponse {
	data: AiGenerationQuestion[]
	meta: Meta
}
